import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Popconfirm,
  message,
  Spin,
  DatePicker,
  Switch,
} from "antd";
import api from "../services/api";
import moment from "moment";

const { Option } = Select;

const Sliders = () => {
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingSlider, setEditingSlider] = useState(null);

  // Fetch sliders
  const fetchSliders = async () => {
    setLoading(true);
    try {
      const res = await api.get("/sliders");
      setSliders(Array.isArray(res.data) ? res.data : res.data.data || []);
    } catch {
      message.error("Failed to fetch sliders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSliders();
  }, []);

  const handleSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        start_date: values.start_date ? values.start_date.toISOString() : null,
        end_date: values.end_date ? values.end_date.toISOString() : null,
      };

      if (editingSlider) {
        await api.put(`/sliders/${editingSlider.id}`, payload);
        message.success("Slider updated");
      } else {
        await api.post("/sliders", payload);
        message.success("Slider added");
      }
      fetchSliders();
      setIsModalVisible(false);
      form.resetFields();
      setEditingSlider(null);
    } catch {
      message.error("Operation failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/sliders/${id}`);
      message.success("Slider deleted");
      fetchSliders();
    } catch {
      message.error("Delete failed");
    }
  };

  const columns = [
    { title: "Title", dataIndex: "title", key: "title", width: 150 },
    { title: "Type", dataIndex: "type", key: "type", width: 120 },
    {
      title: "Active",
      dataIndex: "is_active",
      key: "is_active",
      width: 80,
      render: (text) => (text ? "Yes" : "No"),
    },
    {
      title: "Start",
      dataIndex: "start_date",
      width: 120,
      render: (text) => (text ? moment(text).format("YYYY-MM-DD") : "-"),
    },
    {
      title: "End",
      dataIndex: "end_date",
      width: 120,
      render: (text) => (text ? moment(text).format("YYYY-MM-DD") : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            type="link"
            size="small"
            onClick={() => {
              setEditingSlider(record);
              form.setFieldsValue({
                ...record,
                start_date: record.start_date ? moment(record.start_date) : null,
                end_date: record.end_date ? moment(record.end_date) : null,
              });
              setIsModalVisible(true);
            }}
          >
            Edit
          </Button>

          <Popconfirm
            title="Delete this slider?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="link" size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: 10 }}>
      {/* Header (responsive) */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          gap: 10,
          marginBottom: 12,
        }}
      >
        <h2 style={{ fontSize: 18, margin: 0 }}>Sliders Management</h2>

        <Button
          type="primary"
          style={{ width: "100%", maxWidth: 200 }}
          onClick={() => {
            setIsModalVisible(true);
            setEditingSlider(null);
            form.resetFields();
          }}
        >
          + Add Slider
        </Button>
      </div>

      {/* Table with horizontal scroll for mobile */}
      {loading ? (
        <Spin />
      ) : (
        <Table
          columns={columns}
          dataSource={sliders}
          rowKey="id"
          bordered
          scroll={{ x: "max-content" }}
          size="small"
        />
      )}

      {/* Modal */}
      <Modal
        title={editingSlider ? "Edit Slider" : "Add Slider"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => form.submit()}
        okText={editingSlider ? "Update" : "Add"}
        width="95%"
        style={{ top: 10 }}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true }]}
          >
            <Input placeholder="Enter title" />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item
            label="Type"
            name="type"
            rules={[{ required: true }]}
          >
            <Select placeholder="Select type">
              <Option value="home">Home</Option>
              <Option value="tournament">Tournament</Option>
              <Option value="contest">Contest</Option>
              <Option value="offer">Offer</Option>
              <Option value="sponsor">Sponsor</Option>
              <Option value="news">News</Option>
              <Option value="custom">Custom</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Image URL"
            name="image_url"
            rules={[{ required: true }]}
          >
            <Input placeholder="Enter image URL" />
          </Form.Item>

          <Form.Item
            label="Active"
            name="is_active"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>

          <Form.Item label="Start Date" name="start_date">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item label="End Date" name="end_date">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            label="Platform"
            name="platform"
            initialValue="both"
          >
            <Select>
              <Option value="web">Web</Option>
              <Option value="mobile">Mobile</Option>
              <Option value="both">Both</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Sliders;
